import { useEffect, useMemo, useState } from "react";
import { Download, QrCode, Loader2 } from "lucide-react";
import { toast } from "sonner";
import QRCode from "qrcode";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const fileUrl = (path) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`;

const buildLink = (url, code) => {
  if (!url) return "";
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}ref=${encodeURIComponent(code || "")}`;
};

const loadImage = (src) =>
  new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });

const roundRect = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const triggerDownload = (blob, filename) => {
  const a = document.createElement("a");
  const u = URL.createObjectURL(blob);
  a.href = u;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(u), 1000);
};

export default function ReferralToolkit({ referralCode, posters = [] }) {
  const [products, setProducts] = useState([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [busy, setBusy] = useState(null); // resource id being composited

  useEffect(() => {
    api.get("/referral-products").then((r) => {
      const list = r.data.products || [];
      setProducts(list);
      const ideathon = list.find((p) => /ideathon/i.test(p.product_key) || /ideathon/i.test(p.product_name));
      setSelectedKey(ideathon?.product_key || list[0]?.product_key || "");
    });
  }, []);

  const selected = useMemo(() => products.find((p) => p.product_key === selectedKey), [products, selectedKey]);
  const personalLink = selected ? buildLink(selected.product_url, referralCode) : "";

  // (re)generate QR whenever the chosen product / code changes
  useEffect(() => {
    if (!personalLink) {
      setQrDataUrl("");
      return;
    }
    QRCode.toDataURL(personalLink, {
      width: 720,
      margin: 1,
      errorCorrectionLevel: "M",
      color: { dark: "#0A0E1A", light: "#FFFFFF" },
    })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(""));
  }, [personalLink]);

  // ---- standalone branded QR card download ----
  const downloadStandalone = async () => {
    if (!qrDataUrl) return;
    try {
      const W = 760, H = 940;
      const canvas = document.createElement("canvas");
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#0A0E1A"; ctx.fillRect(0, 0, W, H);

      // header label
      ctx.fillStyle = "#F97316";
      ctx.font = "700 30px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText((selected?.product_name || "NoobSync").toUpperCase(), W / 2, 86);

      // white card with QR
      const cardSize = 560, cardX = (W - cardSize) / 2, cardY = 130;
      ctx.save();
      roundRect(ctx, cardX, cardY, cardSize, cardSize, 28);
      ctx.fillStyle = "#FFFFFF"; ctx.fill();
      ctx.restore();
      const qrImg = await loadImage(qrDataUrl);
      const qrInset = 40;
      ctx.drawImage(qrImg, cardX + qrInset, cardY + qrInset, cardSize - qrInset * 2, cardSize - qrInset * 2);

      // CTA
      ctx.fillStyle = "#FAFAF9";
      ctx.font = "800 46px sans-serif";
      ctx.fillText("SCAN TO REGISTER", W / 2, cardY + cardSize + 86);

      // ref code chip
      ctx.fillStyle = "#A1A1AA";
      ctx.font = "600 26px monospace";
      ctx.fillText(`Ref: ${referralCode || ""}`, W / 2, cardY + cardSize + 134);

      await new Promise((res) => canvas.toBlob((b) => { if (b) triggerDownload(b, `noobsync-qr-${selectedKey}.png`); res(); }, "image/png"));
      toast.success("QR downloaded");
    } catch (e) {
      toast.error("Could not generate QR");
    }
  };

  // ---- composite personal QR onto a poster at the admin-configured zone ----
  const downloadPoster = async (r) => {
    const conf = r.qr_overlay || {};
    // which product this poster's QR points to (admin-pinned, else the dropdown choice)
    const prodKey = conf.product_key || selectedKey;
    const prod = products.find((p) => p.product_key === prodKey);
    const link = prod ? buildLink(prod.product_url, referralCode) : personalLink;
    if (!link) return toast.error("Pick a product first");
    setBusy(r.id);
    try {
      const qrUrl = await QRCode.toDataURL(link, { width: 720, margin: 1, errorCorrectionLevel: "M", color: { dark: "#0A0E1A", light: "#FFFFFF" } });
      const resp = await fetch(fileUrl(r.file_path));
      if (!resp.ok) throw new Error("fetch failed");
      const blob = await resp.blob();
      const bmp = await createImageBitmap(blob);
      const w = bmp.width, h = bmp.height;
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(bmp, 0, 0, w, h);

      const qrImg = await loadImage(qrUrl);
      // admin-drawn square box (size is a fraction of width)
      const box = Math.max(1, (conf.size || 0.26) * w);
      const bx = (conf.x ?? 0.68) * w;
      const by = (conf.y ?? 0.68) * h;
      const showLabel = conf.label !== false;
      const cover = conf.cover !== false;

      if (cover) {
        ctx.save();
        ctx.shadowColor = "rgba(0,0,0,0.4)";
        ctx.shadowBlur = box * 0.06;
        roundRect(ctx, bx, by, box, box, box * 0.08);
        ctx.fillStyle = "#FFFFFF"; ctx.fill();
        ctx.restore();
      }
      const qrArea = showLabel ? box * 0.76 : box * 0.9;
      const qx = bx + (box - qrArea) / 2;
      const qy = by + box * (showLabel ? 0.06 : 0.05);
      ctx.drawImage(qrImg, qx, qy, qrArea, qrArea);
      if (showLabel) {
        ctx.fillStyle = "#0A0E1A";
        ctx.font = `800 ${Math.round(box * 0.085)}px sans-serif`;
        ctx.textAlign = "center";
        ctx.fillText("SCAN TO REGISTER", bx + box / 2, by + box * 0.93);
      }

      const base = (r.name || "poster").replace(/[^A-Za-z0-9._-]+/g, "_");
      await new Promise((res) => canvas.toBlob((b) => { if (b) triggerDownload(b, `${base}-my-qr.png`); res(); }, "image/png"));
      toast.success("Poster with your QR downloaded");
    } catch (e) {
      toast.error("Could not build poster — try the standalone QR instead");
    } finally {
      setBusy(null);
    }
  };

  if (!products.length) return null;

  return (
    <div className="rounded-2xl border border-[#F97316]/30 bg-gradient-to-br from-[#F97316]/[0.06] via-zinc-950 to-zinc-950 p-6" data-testid="referral-toolkit">
      <div className="flex items-center gap-2">
        <QrCode className="h-5 w-5 text-[#F97316]" />
        <h3 className="font-display text-xl font-bold">Your QR & personalized posters</h3>
      </div>
      <p className="mt-1 text-sm text-zinc-500">
        Every QR below encodes <span className="text-[#F97316]">your</span> referral link — scans are tracked to you automatically.
      </p>

      <div className="mt-4 grid gap-5 md:grid-cols-[260px_1fr]">
        {/* left — product picker + standalone QR */}
        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Link this QR points to</label>
            <Select value={selectedKey} onValueChange={setSelectedKey}>
              <SelectTrigger className="border-zinc-800 bg-zinc-900" data-testid="qr-product-select"><SelectValue placeholder="Pick a product" /></SelectTrigger>
              <SelectContent>
                {products.map((p) => (
                  <SelectItem key={p.product_key} value={p.product_key}>{p.product_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col items-center rounded-xl border border-zinc-800 bg-white p-3">
            {qrDataUrl ? (
              <img src={qrDataUrl} alt="Your referral QR" className="h-40 w-40" data-testid="qr-preview" />
            ) : (
              <div className="flex h-40 w-40 items-center justify-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
            )}
          </div>
          <button
            onClick={downloadStandalone}
            disabled={!qrDataUrl}
            data-testid="qr-download-standalone"
            className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-[#F97316] px-4 py-2 text-sm font-bold text-white hover:bg-[#ea6a0e] disabled:opacity-50"
          >
            <Download className="h-4 w-4" /> Download my QR (PNG)
          </button>
          {personalLink && (
            <p className="break-all text-center text-[10px] text-zinc-600">{personalLink}</p>
          )}
        </div>

        {/* right — posters with QR baked in */}
        <div>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-zinc-500">Ready-to-post posters · with your QR</p>
          {posters.length === 0 ? (
            <p className="text-sm text-zinc-500">No QR posters yet — your cohort manager will add them soon.</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2" data-testid="qr-posters">
              {posters.map((b) => (
                <div key={b.id} className="flex items-center gap-3 rounded-lg border border-zinc-800 bg-zinc-900 p-2.5">
                  <img src={fileUrl(b.file_path)} alt="" className="h-12 w-16 shrink-0 rounded border border-zinc-700 object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold text-zinc-200">{b.name}</p>
                  </div>
                  <button
                    onClick={() => downloadPoster(b)}
                    disabled={busy === b.id}
                    data-testid={`qr-poster-download-${b.id}`}
                    className="inline-flex shrink-0 items-center gap-1 rounded border border-[#F97316]/40 bg-[#F97316]/10 px-2.5 py-1.5 text-[11px] font-bold text-[#F97316] hover:bg-[#F97316]/20 disabled:opacity-50"
                  >
                    {busy === b.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} My QR
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
